import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';

// Retention workflow API layer. Endpoints follow public/docs/RETENTION_WORKFLOW.md §6.
// None of the /retention endpoints exist on the backend yet; views must treat a failed
// response as "not built yet" and render their placeholder, never crash.
// Views check `res.payload.status === 'success'` (request.js returns the error body on non-2xx).

const resource = () => ({ loading: false, data: [], meta: {}, error: null });

const initialState = {
  header: { loading: false, data: null, error: null },
  stages: resource(),
  profile: { loading: false, data: null, error: null },
  tracking: { loading: false, data: null, error: null },
  demo: { loading: false, data: null, error: null },
  reports: resource(),
  touchpoints: resource(),
  timeline: resource(),
  homeTasks: resource(),
  reportsList: resource(),
  retained: resource(),
  exited: resource(),
  saving: false,
};

const toQuery = (params = {}) => {
  const q = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') q.append(k, v);
  });
  const s = q.toString();
  return s ? `?${s}` : '';
};

// ---------- reads (§6.1) ----------

export const getRetentionHeader = createAsyncThunk('retention/getHeader', async (courseEnrollmentId) =>
  apiRequest({ method: 'get', url: `retention/${courseEnrollmentId}/header` }),
);

export const getRetentionStages = createAsyncThunk('retention/getStages', async (courseEnrollmentId) =>
  apiRequest({ method: 'get', url: `retention/${courseEnrollmentId}/stages` }),
);

export const getRetentionProfile = createAsyncThunk('retention/getProfile', async (courseEnrollmentId) =>
  apiRequest({ method: 'get', url: `retention/${courseEnrollmentId}/profile` }),
);

// stages 8–13 in one call: first feedback, reports 1–4, call attempts, exit, renewal, signals, reason options
export const getTracking = createAsyncThunk('retention/getTracking', async (courseEnrollmentId) =>
  apiRequest({ method: 'get', url: `retention/${courseEnrollmentId}/tracking` }),
);

// stages 4–7 in one call: subjects (preferences, active tutor, latest attempt), every demo attempt,
// the go-live checklist and the rejection reasons
export const getDemo = createAsyncThunk('retention/getDemo', async (courseEnrollmentId) =>
  apiRequest({ method: 'get', url: `retention/${courseEnrollmentId}/demo` }),
);

export const getEnrollmentReports = createAsyncThunk(
  'retention/getEnrollmentReports',
  async ({ courseEnrollmentId, report_type }) =>
    apiRequest({ method: 'get', url: `retention/${courseEnrollmentId}/reports${toQuery({ report_type })}` }),
);

export const getTouchpoints = createAsyncThunk(
  'retention/getTouchpoints',
  async ({ courseEnrollmentId, touchpoint_type }) =>
    apiRequest({ method: 'get', url: `retention/${courseEnrollmentId}/touchpoints${toQuery({ touchpoint_type })}` }),
);

export const getRetentionTimeline = createAsyncThunk('retention/getTimeline', async (courseEnrollmentId) =>
  apiRequest({ method: 'get', url: `retention/${courseEnrollmentId}/timeline` }),
);

export const getHomeTasks = createAsyncThunk('retention/getHomeTasks', async (params = {}) =>
  apiRequest({ method: 'get', url: `retention/home-tasks${toQuery(params)}` }),
);

export const getRetentionReportsList = createAsyncThunk('retention/getReportsList', async (params = {}) =>
  apiRequest({ method: 'get', url: `retention/reports${toQuery(params)}` }),
);

export const getRetainedStudents = createAsyncThunk('retention/getRetained', async (params = {}) =>
  apiRequest({ method: 'get', url: `retention/retained${toQuery(params)}` }),
);

export const getExitedStudents = createAsyncThunk('retention/getExited', async (params = {}) =>
  apiRequest({ method: 'get', url: `retention/exited${toQuery(params)}` }),
);

// ---------- writes (§6.2) ----------

export const advanceStage = createAsyncThunk('retention/advanceStage', async ({ courseEnrollmentId, ...data }) =>
  apiRequest({ method: 'post', url: `retention/${courseEnrollmentId}/advance-stage`, data }),
);

export const saveRetentionProfile = createAsyncThunk('retention/saveProfile', async ({ courseEnrollmentId, ...data }) =>
  apiRequest({ method: 'put', url: `retention/${courseEnrollmentId}/profile`, data }),
);

export const saveInduction = createAsyncThunk('retention/saveInduction', async ({ courseEnrollmentId, ...data }) =>
  apiRequest({ method: 'put', url: `retention/${courseEnrollmentId}/induction`, data }),
);

export const createDemoAttempt = createAsyncThunk(
  'retention/createDemoAttempt',
  async ({ courseEnrollmentId, ...data }) =>
    apiRequest({ method: 'post', url: `retention/${courseEnrollmentId}/demo-attempt`, data }),
);

export const scheduleDemo = createAsyncThunk('retention/scheduleDemo', async ({ attemptId, ...data }) =>
  apiRequest({ method: 'put', url: `retention/demo-attempt/${attemptId}/schedule`, data }),
);

// Tutor side is on hold: the CRO records the tutor's response (stage 5) and 6a feedback on their behalf.
export const saveTutorResponse = createAsyncThunk('retention/saveTutorResponse', async ({ attemptId, ...data }) =>
  apiRequest({ method: 'put', url: `retention/demo-attempt/${attemptId}/tutor-response`, data }),
);

export const saveTutorFeedback = createAsyncThunk('retention/saveTutorFeedback', async ({ attemptId, ...data }) =>
  apiRequest({ method: 'put', url: `retention/demo-attempt/${attemptId}/tutor-feedback`, data }),
);

// The CRO records the parent's phone feedback (stored as parent_feedback_by_type 1, §9).
export const saveParentDemoFeedback = createAsyncThunk(
  'retention/saveParentDemoFeedback',
  async ({ attemptId, ...data }) =>
    apiRequest({ method: 'put', url: `retention/demo-attempt/${attemptId}/parent-feedback`, data }),
);

export const remapDemoAttempt = createAsyncThunk('retention/remap', async ({ attemptId, ...data }) =>
  apiRequest({ method: 'post', url: `retention/demo-attempt/${attemptId}/remap`, data }),
);

export const saveAdmissionChecklist = createAsyncThunk(
  'retention/saveAdmissionChecklist',
  async ({ courseEnrollmentId, ...data }) =>
    apiRequest({ method: 'put', url: `retention/${courseEnrollmentId}/admission-checklist`, data }),
);

export const createTouchpoint = createAsyncThunk('retention/createTouchpoint', async ({ courseEnrollmentId, ...data }) =>
  apiRequest({ method: 'post', url: `retention/${courseEnrollmentId}/touchpoint`, data }),
);

export const updateTouchpoint = createAsyncThunk('retention/updateTouchpoint', async ({ touchpointId, ...data }) =>
  apiRequest({ method: 'put', url: `retention/touchpoint/${touchpointId}`, data }),
);

export const saveReportSuggestions = createAsyncThunk('retention/saveSuggestions', async ({ reportId, ...data }) =>
  apiRequest({ method: 'put', url: `retention/report/${reportId}/suggestions`, data }),
);

export const saveFirstFeedback = createAsyncThunk('retention/saveFirstFeedback', async ({ courseEnrollmentId, ...data }) =>
  apiRequest({ method: 'put', url: `retention/${courseEnrollmentId}/first-feedback`, data }),
);

export const saveReport = createAsyncThunk('retention/saveReport', async ({ courseEnrollmentId, reportType, ...data }) =>
  apiRequest({ method: 'put', url: `retention/${courseEnrollmentId}/report/${reportType}`, data }),
);

export const logCallAttempt = createAsyncThunk('retention/logCallAttempt', async ({ courseEnrollmentId, ...data }) =>
  apiRequest({ method: 'post', url: `retention/${courseEnrollmentId}/call-attempt`, data }),
);

// body: { exit_type: 3 | 4, course_inactive_date, ...exit fields }
export const recordExit = createAsyncThunk('retention/recordExit', async ({ courseEnrollmentId, ...data }) =>
  apiRequest({ method: 'post', url: `retention/${courseEnrollmentId}/exit`, data }),
);

export const shareReport = createAsyncThunk('retention/shareReport', async (reportId) =>
  apiRequest({ method: 'put', url: `retention/report/${reportId}/share` }),
);

export const remindParent = createAsyncThunk('retention/remindParent', async (reportId) =>
  apiRequest({ method: 'post', url: `retention/report/${reportId}/remind` }),
);

export const renewEnrollment = createAsyncThunk('retention/renew', async ({ courseEnrollmentId, ...data }) =>
  apiRequest({ method: 'post', url: `retention/${courseEnrollmentId}/renew`, data }),
);

// ---------- slice ----------

const ok = (payload) => payload?.status === 'success';

const readCases = (builder, thunk, key, { single = false } = {}) => {
  builder
    .addCase(thunk.pending, (state) => {
      state[key].loading = true;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      state[key].loading = false;
      // error.reason: 'forbidden' | 'not_found' | 'schema_missing' | 'error' (retention.controller sendError)
      state[key].error = ok(action.payload)
        ? null
        : { reason: action.payload?.data?.reason || 'error', message: action.payload?.message || 'Request failed' };
      if (single) {
        state[key].data = ok(action.payload) ? action.payload.data : null;
      } else {
        state[key].data = ok(action.payload) ? action.payload.data || [] : [];
        state[key].meta = ok(action.payload) ? action.payload.meta || {} : {};
      }
    })
    .addCase(thunk.rejected, (state, action) => {
      state[key].loading = false;
      state[key].error = { reason: 'error', message: action.error?.message || 'Request failed' };
    });
};

const writeThunks = [
  advanceStage,
  saveRetentionProfile,
  saveInduction,
  createDemoAttempt,
  scheduleDemo,
  saveTutorResponse,
  saveTutorFeedback,
  saveParentDemoFeedback,
  remapDemoAttempt,
  saveAdmissionChecklist,
  createTouchpoint,
  updateTouchpoint,
  saveReportSuggestions,
  saveFirstFeedback,
  saveReport,
  logCallAttempt,
  recordExit,
  shareReport,
  remindParent,
  renewEnrollment,
];

const retentionSlice = createSlice({
  name: 'retention',
  initialState,
  reducers: {
    resetRetentionWorkspace: () => initialState,
  },
  extraReducers: (builder) => {
    readCases(builder, getRetentionHeader, 'header', { single: true });
    readCases(builder, getRetentionStages, 'stages');
    readCases(builder, getRetentionProfile, 'profile', { single: true });
    readCases(builder, getTracking, 'tracking', { single: true });
    readCases(builder, getDemo, 'demo', { single: true });
    readCases(builder, getEnrollmentReports, 'reports');
    readCases(builder, getTouchpoints, 'touchpoints');
    readCases(builder, getRetentionTimeline, 'timeline');
    readCases(builder, getHomeTasks, 'homeTasks');
    readCases(builder, getRetentionReportsList, 'reportsList');
    readCases(builder, getRetainedStudents, 'retained');
    readCases(builder, getExitedStudents, 'exited');
    writeThunks.forEach((thunk) => {
      builder
        .addCase(thunk.pending, (state) => {
          state.saving = true;
        })
        .addCase(thunk.fulfilled, (state) => {
          state.saving = false;
        })
        .addCase(thunk.rejected, (state) => {
          state.saving = false;
        });
    });
  },
});

export const { resetRetentionWorkspace } = retentionSlice.actions;
export default retentionSlice.reducer;
